import {
  CancelRounded,
  ChangeCircle,
  DeleteRounded,
  SaveRounded,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Fab,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  Slide,
  Switch,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { createTheme } from "@mui/system";
import { TimePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import moment from "moment";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { getObjectElement } from "src/utils/dataRenders";
import { Yup } from "src/utils/utils";
import CustomDatePicker from "../CustomDatePicker";
import FilesDropzone from "../FilesDropzone";
import LoadingScreen from "../LoadingScreen";
import CustomTextEditor from "./CustomTextEditor";

const theme = createTheme();
const useStyles = makeStyles(() => ({
  smallInput: {
    "& .MuiInputBase-input": {
      padding: `${theme.spacing(0.5, 1)} !important`,
      fontSize: "0.875rem",
      lineHeight: "1.2",
    },
    "& .MuiInputBase-root": {
      minHeight: "32px",
    },
    "& .MuiOutlinedInput-root": {
      padding: "0 !important",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      transform: "translate(14px, 8px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.75rem",
      marginTop: "2px",
      marginLeft: "4px",
    },
    "& .MuiAutocomplete-inputRoot": {
      padding: "0 !important",
      paddingRight: "39px !important",
    },
    "& .MuiSelect-select": {
      padding: `${theme.spacing(0.5, 1)} !important`,
    },
  },
}));

const fieldTypeMap = {
  currency: "number",
  decimal: "number",
};

const getFieldPaperSx = (size) => ({
  p: size === "small" ? { xs: 0.5, sm: 0.75 } : { xs: 1.25, sm: 1.5 },
  borderRadius: 2,
  // border: "1px solid",
  // borderColor: "divider",
  backgroundColor: "background.paper",
  transition:
    "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
  "&:hover": {
    boxShadow: 4,
    transform: "translateY(-2px)",
    borderColor: "primary.light",
  },
});

const inputSx = {
  "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
    borderRadius: 2,
    transition: "box-shadow 140ms ease, transform 140ms ease",
    "&:hover": { boxShadow: 1 },
    "&.Mui-focused": { boxShadow: 4, transform: "translateY(-1px)" },
  },
};

const RenderFieldInput = ({
  field,
  values,
  errors,
  touched,
  setFieldValue,
  handleChange,
  size,
  handleBlur,
  index = 0,
}) => {
  const classes = useStyles();
  const mainClass = classes.smallInput;
  const { t } = useTranslation();
  const debounce = useRef(null);
  const filterOpts = createFilterOptions();

  const delay = Math.min(150 + index * 60, 700);

  const wrap = (child, gridProps = {}) => (
    <Slide in direction="up" timeout={delay} mountOnEnter unmountOnExit>
      <Grid item xs={12} {...gridProps}>
        <Paper elevation={0} sx={getFieldPaperSx(size)}>
          {child}
        </Paper>
      </Grid>
    </Slide>
  );

  const renderHelperText = (field, values, errors, touched) => {
    const helperSx = { mb: size === "small" ? "0.25rem" : "0.75rem" };
    if (errors[field.id] && touched[field.id]) {
      return (
        <FormHelperText error sx={helperSx}>
          {errors[field.id]}
        </FormHelperText>
      );
    }
    if (!field.helperText) return null;
    if (typeof field.helperText === "function") {
      const hText = field.helperText(values);
      if (React.isValidElement(hText)) return hText;
      return <FormHelperText sx={helperSx}>{hText}</FormHelperText>;
    }
    if (React.isValidElement(field.helperText)) return field.helperText;
    return <FormHelperText sx={helperSx}>{field.helperText}</FormHelperText>;
  };

  if (field.type === "file") {
    if (values[field.id]) {
      return wrap(
        <>
          <Typography fontWeight={600} sx={{ mb: 0.5 }}>
            {field.label}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {values[field.id][0].name}
          </Typography>
          {!field.readOnly && (
            <Button
              color="error"
              size="small"
              startIcon={<ChangeCircle />}
              onClick={() => {
                setFieldValue(field.id, null);
              }}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                "&:hover": { transform: "scale(1.02)" },
                transition: "transform 120ms ease",
              }}
              variant="outlined"
            >
              {t("Cambiar")}
            </Button>
          )}
        </>,
      );
    }
    return wrap(
      <FilesDropzone
        message={field.label}
        uploadOnSelect
        onUpload={(files) => setFieldValue(field.id, files)}
        accept={field.options?.accept}
      />,
    );
  }

  if (field.type === "richText") {
    return wrap(
      <>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          {field.label}
        </Typography>
        <CustomTextEditor
          disabled={field.readOnly}
          label={field.label}
          initialValue={values[field.id]}
          {...field.options}
          onChange={(v) => setFieldValue(field.id, v)}
        />
        {renderHelperText(field, values, errors, touched)}
      </>,
    );
  }

  if (field.type === "textarea") {
    return wrap(
      <>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          {field.label}
        </Typography>
        <CustomTextEditor
          textarea
          {...field.options}
          disabled={field.readOnly}
          label={field.label}
          initialValue={values[field.id]}
          onChange={(v, { text }) => setFieldValue(field.id, text)}
        />
        {renderHelperText(field, values, errors, touched)}
      </>,
    );
  }

  if (field.type === "divider") {
    return (
      <Slide in direction="up" timeout={delay} mountOnEnter unmountOnExit>
        <Grid item xs={12}>
          {field.label && (
            <Typography
              variant="h6"
              sx={{
                color: "text.primary",
                fontWeight: 700,
                letterSpacing: 0.2,
                mb: 0.5,
              }}
            >
              {field.label}
            </Typography>
          )}
          <Box
            sx={{
              height: 1,
              background:
                "linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)",
              borderRadius: 1,
              mb: 1,
            }}
          />
        </Grid>
      </Slide>
    );
  }

  if (field.type === "select") {
    return wrap(
      <>
        <Autocomplete
          className={mainClass}
          fullWidth
          disableClearable={!field.optional}
          disabled={field.readOnly}
          onChange={(e, v) => {
            if (v && v.inputValue) {
              const _opts = field.options.filter(
                (o) => o.value !== v.inputValue,
              );
              setFieldValue(field.id, v);
              field.options = _opts;
            } else {
              setFieldValue(field.id, v);
            }
          }}
          loading={field.isSearching}
          value={values[field.id]}
          size={size || "medium"}
          getOptionLabel={(o) => {
            if (typeof o === "string") return o;
            if (o?.inputValue) return o.label;
            if (o?.label) return o.label;
            if (field.getOptionLabel) return field.getOptionLabel(o);
            if (field.initialValue && field.options) {
              const _fo = field.options.find(
                (_o) => _o.value === field.initialValue,
              );
              if (_fo) return _fo.label;
            }
            return "";
          }}
          filterOptions={(options, params) => {
            const filtered = filterOpts(options, params);
            const { inputValue } = params;
            const isExisting = options.some(
              (option) => inputValue === option.label,
            );
            if (inputValue !== "" && !isExisting && field.allowAdd) {
              filtered.push({
                inputValue,
                label: t("Agregar") + ` "${inputValue}"`,
                value: inputValue,
              });
            }
            return filtered;
          }}
          isOptionEqualToValue={(o, v) => {
            if (v?.inputValue) return v.inputValue.startsWith(o.label);
            return o?.value === v?.value || o === v;
          }}
          options={field.options || []}
          id={field.id}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                key={`${field.id}-select`}
                className={mainClass}
                label={field.label}
                error={Boolean(errors[field.id] && touched[field.id])}
                size={size || "medium"}
                sx={inputSx}
              />
              {renderHelperText(field, values, errors, touched)}
            </>
          )}
        />
      </>,
    );
  }

  if (field.type === "bool" || field.type === "boolean") {
    return wrap(
      <>
        <FormControlLabel
          className={mainClass}
          label={field.label}
          control={
            <Switch
              disabled={field.readOnly}
              checked={Boolean(values[field.id])}
              onChange={() => setFieldValue(field.id, !values[field.id])}
              inputProps={{ "aria-label": field.label }}
              color="success"
            />
          }
        />
        {renderHelperText(field, values, errors, touched)}
      </>,
    );
  }

  if (field.type === "search") {
    return wrap(
      <>
        <Autocomplete
          className={mainClass}
          loading={field.isSearching}
          fullWidth
          disabled={field.readOnly}
          getOptionLabel={field.getOptionLabel}
          options={field.options || []}
          disableClearable
          value={values[field.id]}
          size={size || "medium"}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          onChange={(e, v) => setFieldValue(field.id, v)}
          renderInput={(params) => (
            <>
              <TextField
                className={mainClass}
                {...params}
                InputProps={{ ...params.InputProps, type: "search" }}
                error={Boolean(errors[field.id] && touched[field.id])}
                label={field.label}
                size={size || "medium"}
                sx={inputSx}
                onChange={(e) => {
                  if (e.target.value && e.target.value.length > 2) {
                    if (debounce.current) clearTimeout(debounce.current);
                    debounce.current = setTimeout(() => {
                      field.onSearch?.(e.target.value);
                    }, 500);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (debounce.current) clearTimeout(debounce.current);
                    debounce.current = setTimeout(() => {
                      field.onSearch?.(e.target.value);
                    }, 500);
                  }
                }}
              />
              {renderHelperText(field, values, errors, touched)}
            </>
          )}
        />
      </>,
    );
  }

  if (field.type === "date") {
    return wrap(
      <>
        <CustomDatePicker
          label={field.label}
          id={field.id}
          locale={"es-VE"}
          onChange={(v) => {
            if (v && v.toString() === "Invalid Date") {
              setFieldValue(field.id, null);
            } else {
              setFieldValue(field.id, v);
            }
          }}
          value={values[field.id]}
          disabled={field.readOnly}
          size={size || "medium"}
          sx={inputSx}
          {...field.props}
        />
        {renderHelperText(field, values, errors, touched)}
      </>,
    );
  }

  if (field.type === "time") {
    return wrap(
      <>
        <TimePicker
          label={field.label}
          id={field.id}
          locale={"es-VE"}
          defaultTime={values[field.id]}
          onChange={(v) => setFieldValue(field.id, v)}
          value={values[field.id]}
          disabled={field.readOnly}
          {...field.props}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                className={mainClass}
                size={size || "medium"}
                error={Boolean(errors[field.id] && touched[field.id])}
                sx={inputSx}
              />
              {renderHelperText(field, values, errors, touched)}
            </>
          )}
        />
      </>,
    );
  }

  return wrap(
    <>
      <TextField
        className={mainClass}
        label={field.label}
        variant={field.type === "date" ? "standard" : "outlined"}
        id={field.id}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values[field.id]}
        error={Boolean(errors[field.id] && touched[field.id])}
        fullWidth
        maxRows={field.type === "textarea" ? 6 : 1}
        minRows={field.type === "textarea" ? 2 : 1}
        multiline={field.type === "textarea"}
        inputProps={{
          maxLength: field.maxLength,
          minLength: field.minLength,
          "aria-label": field.label,
        }}
        type={fieldTypeMap[field.type] || field.type || "text"}
        disabled={field.readOnly}
        size={size || "medium"}
        sx={inputSx}
      />
      {renderHelperText(field, values, errors, touched)}
    </>,
  );
};

const RenderForm = ({
  title,
  fieldSet,
  element,
  onCancel,
  onChange,
  showActions,
  onSubmit,
  disabled,
  onDelete,
  initialValues,
  validationSchema,
  size,
  buttonsProps,
  sx,
  children,
}) => {
  const { t } = useTranslation();
  const formikHandle = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const _values = {};
      fieldSet.forEach((f) => {
        if (showField(f) && f.type !== "divider") {
          if (f.type === "date") {
            try {
              const date_value = moment(values[f.id]).format("YYYY-MM-DD");
              _values[f.id] = date_value === "Invalid date" ? null : date_value;
            } catch (e) {
              _values[f.id] = null;
            }
          } else {
            _values[f.id] = values[f.id];
          }
        }
      });
      onSubmit(_values);
    },
    validateOnChange: true,
  });

  const showField = (f) =>
    !f?.conditionalBy || Boolean(f.conditionalBy(formikHandle.values));

  useEffect(() => {
    if (onChange) {
      const res = onChange(formikHandle.values);
      if (res && res !== formikHandle.values && typeof res === "object") {
        formikHandle.setValues(res);
      }
    }
  }, [formikHandle.values]);

  return (
    <Box
      component="form"
      onSubmit={formikHandle.handleSubmit}
      noValidate
      autoComplete="off"
    >
      <Box
        sx={{
          maxWidth: { xs: "100%", md: 1100 },
          mx: "auto",
          px: { xs: 1, sm: 2 },
          ...sx,
        }}
      >
        <Slide in direction="down" timeout={300} mountOnEnter unmountOnExit>
          <Box>
            {title && (
              <Paper
                elevation={0}
                sx={{
                  mb: 1.5,
                  p: { xs: 1.25, sm: 1.5 },
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.9), rgba(250,250,250,0.8))",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {title}
                </Typography>
              </Paper>
            )}

            <Paper
              elevation={0}
              sx={{
                p:
                  size === "small"
                    ? { xs: 0.5, md: 1, lg: 1.5 }
                    : { xs: 1, md: 2, lg: 3 },
                borderRadius: 2,
                border: "none !important",
                borderColor: "divider",
                backgroundColor: "background.paper",
                boxShadow: "none !important",
                // transition: "box-shadow 180ms ease, transform 180ms ease",
                "&:hover": { boxShadow: 3 },
                "& .MuiPaper-root": { boxShadow: "none !important" },
              }}
            >
              <Grid
                container
                spacing={size === "small" ? 0.5 : 1.2}
                sx={{
                  display: "flex",
                  margin: "0px auto !important",
                  justifyContent: "space-between",
                }}
                maxWidth="md"
              >
                {fieldSet.map((f, i) => {
                  if (!f) return null;
                  if (!showField(f)) return null;
                  return (
                    <RenderFieldInput
                      key={i}
                      field={f}
                      {...formikHandle}
                      size={size}
                      index={i}
                    />
                  );
                })}
              </Grid>
            </Paper>
          </Box>
        </Slide>

        {showActions && (
          <Grid
            container
            spacing={1}
            sx={{
              display: "flex",
              margin: "0px auto !important",
              justifyContent:
                onCancel || onDelete ? "space-between" : "flex-end",
              py: { xs: 1, md: 1.5 },
            }}
            maxWidth="md"
          >
            {onCancel ? (
              <Zoom
                timeout={500}
                in={Boolean(onCancel)}
                style={{ transitionDelay: `300ms` }}
                unmountOnExit
              >
                <Fab
                  variant="extended"
                  size="medium"
                  onClick={() => onCancel()}
                  color={buttonsProps?.cancel?.color || "secondary"}
                  sx={{
                    backgroundColor: "background.paper",
                    color: "error.main",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    boxShadow: 0,
                    "&:hover": {
                      backgroundColor: "background.paper",
                      color: "error.main",
                      transform: "scale(1.03)",
                      boxShadow: 3,
                    },
                    transition: "transform 120ms ease, box-shadow 120ms ease",
                  }}
                >
                  {buttonsProps?.cancel?.icon || <CancelRounded />}
                  <Typography variant="caption" sx={{ ml: 0.75 }}>
                    {buttonsProps?.cancel?.label || t("Cancelar")}
                  </Typography>
                </Fab>
              </Zoom>
            ) : (
              <></>
            )}

            {element && onDelete && (
              <Zoom
                in={Boolean(onDelete)}
                style={{ transitionDelay: `300ms` }}
                unmountOnExit
              >
                <Button
                  startIcon={buttonsProps?.delete?.icon || <DeleteRounded />}
                  onClick={() => onDelete()}
                  color={buttonsProps?.delete?.color || "error"}
                  variant={buttonsProps?.delete?.variant || "outlined"}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    "&:hover": { transform: "scale(1.02)" },
                    transition: "transform 120ms ease",
                  }}
                >
                  {buttonsProps?.delete?.label || t("delete")}
                </Button>
              </Zoom>
            )}

            <Zoom in={Boolean(onSubmit)} style={{ transitionDelay: `300ms` }}>
              <Fab
                variant="extended"
                size="medium"
                color={buttonsProps?.submit?.color || "success"}
                type="submit"
                disabled={Boolean(disabled) || !formikHandle.isValid}
                sx={{
                  borderRadius: 2,
                  boxShadow: 1,
                  "&:hover": { transform: "scale(1.03)", boxShadow: 4 },
                  transition: "transform 120ms ease, box-shadow 120ms ease",
                }}
              >
                {buttonsProps?.submit?.icon || <SaveRounded />}
                <Typography variant="caption" sx={{ ml: 0.75 }}>
                  {buttonsProps?.submit?.label || t("Guardar")}
                </Typography>
              </Fab>
            </Zoom>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

const FormBuilder = ({
  title,
  element,
  fieldSet,
  onCancel,
  showActions,
  onChange,
  buttonsProps,
  onSubmit,
  onDelete,
  disabled,
  size,
  sx,
  children,
  refreshCount,
  ref,
}) => {
  const { t } = useTranslation();
  const { adminMode } = useSelector((state) => state.settings);
  const { currentCustomer } = useSelector((state) => state.customers);
  const { user } = useSelector((state) => state.users);
  const initialized = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [validation, setValidation] = useState({});
  const [internalFieldSet, setInternalFieldSet] = useState(fieldSet);

  const validationTypes = {
    text: Yup.string(),
    phone: Yup.string(),
    email: Yup.string(),
    textarea: Yup.string(),
    number: Yup.number(),
    date: Yup.date(),
    currency: Yup.number(),
    decimal: Yup.number(),
    select: Yup.object(),
    bool: Yup.boolean(),
    boolean: Yup.boolean(),
    search: Yup.object(),
    time: Yup.string().test("is_time", t("Debe ser una hora válida"), (v) => {
      return moment(v, "HH:mm");
    }),
    file: Yup.mixed(),
    richText: Yup.string(),
    divider: Yup.string(),
  };

  const getValidation = (f) => {
    if (f.validation) return f.validation;
    if (
      f.optional ||
      f.type === "bool" ||
      f.type === "boolean" ||
      f.type === "divider"
    ) {
      return validationTypes[f.type || "text"].optional().nullable();
    }
    let _v = validationTypes[f.type || "text"];
    if (f.minLength) {
      _v = _v.min(
        f.minLength,
        t("Debe tener al menos {{minLenght}} caracteres", {
          minLenght: f.minLenght,
        }),
      );
    }
    if (f.maxLength) {
      _v = _v.max(
        f.maxLength,
        t("Debe tener como máximo {{maxLength}} caracteres", {
          maxLength: f.maxLength,
        }),
      );
    }
    if (f.min) {
      _v = _v.min(f.min, t("Debe ser mayor o igual a {{min}}", { min: f.min }));
    }
    if (f.max) {
      _v = _v.max(f.max, t("Debe ser menor o igual a {{max}}", { max: f.max }));
    }
    return _v.required(t("Campo requerido"));
  };

  const parseFields = () => {
    const _ini = {};
    const _vali = {};
    const _interFields = [];
    const objectTypes = ["select", "search"];

    fieldSet.map((field) => {
      if (!field || field.type === "divider") return;
      let clonedField = field;
      let fieldValue = field.initialValue || "";

      if (field.type === "bool" || field.type === "boolean") {
        fieldValue = Boolean(field.initialValue);
      }

      if (field.types === "date") {
        fieldValue = field.initialValue
          ? moment(field.initialValue).format("YYYY-MM-DD")
          : "";
        if (fieldValue === "Invalid date") {
          fieldValue = null;
        }
        if (element) {
          fieldValue = element[field.id]
            ? moment(element[field.id]).format("YYYY-MM-DD")
            : "";
          if (fieldValue === "Invalid date") {
            fieldValue = null;
          }
        }
        fieldValue = fieldValue || "";
        _interFields.push({
          ...clonedField,
          type: "date",
          initialValue: fieldValue,
          props: {
            ...clonedField.props,
            format: "YYYY-MM-DD",
          },
        });
        return;
      }

      if (element) {
        fieldValue = element[field.id];
        if (field.getValue) {
          fieldValue = field.getValue(element);
          if (
            objectTypes.includes(field.type) &&
            (!clonedField.options || clonedField.options.length === 0)
          ) {
            clonedField.options = [fieldValue];
          }
        } else {
          if (objectTypes.includes(field.type)) {
            if (fieldValue && typeof fieldValue === "object") {
              fieldValue = fieldValue.id;
            }
            if (field.options) {
              fieldValue = field.options.find(
                (option) => option.value === fieldValue,
              );
            } else {
              if (field.name !== field.id) {
                const nameValue = getObjectElement(element, field.name);
                fieldValue = { value: fieldValue, label: nameValue };
              }
              clonedField.options = [fieldValue];
            }
          }
        }
      } else {
        if (clonedField.options && clonedField.options.length > 0) {
          if (!clonedField.initialValue) {
            clonedField.initialValue = clonedField.options[0];
            fieldValue = clonedField.options[0];
          } else {
            if (!clonedField.initialValue.value) {
              clonedField.initialValue = clonedField.options.find(
                (option) => option.value === clonedField.initialValue,
              );
              fieldValue = clonedField.initialValue;
              if (!clonedField.initialValue) {
                clonedField.initialValue = clonedField.options[0];
                fieldValue = clonedField.options[0];
              }
            }
          }
        }
      }

      _interFields.push(clonedField);
      _ini[field.id] = fieldValue;
      _vali[field.id] = getValidation(clonedField);
    });

    setInternalFieldSet(_interFields);
    setInitialValues(_ini);
    setValidation(Yup.object().shape(_vali));
  };

  useEffect(() => {
    if (initialized.current === null) {
      initialized.current = true;
    }
  }, []);

  useEffect(() => {
    if (fieldSet) {
      parseFields();
    }
  }, [fieldSet, element, refreshCount]);

  if (!initialValues) {
    return <LoadingScreen message={t("Cargando formulario")} />;
  }

  return (
    <RenderForm
      ref={ref}
      title={title}
      onSubmit={onSubmit}
      onCancel={onCancel}
      onChange={onChange}
      onDelete={onDelete}
      element={element}
      fieldSet={fieldSet}
      disabled={disabled}
      initialValues={initialValues}
      showActions={showActions}
      buttonsProps={buttonsProps}
      validationSchema={validation}
      sx={sx}
      size={size}
      children={children}
    />
  );
};

const formField = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.oneOf([
    "text",
    "email",
    "textarea",
    "richText",
    "number",
    "currency",
    "select",
    "bool",
    "boolean",
    "search",
    "date",
    "file",
    "time",
    "divider",
  ]).isRequired,
  options: PropTypes.any,
  getOptionLabel: PropTypes.func,
  initialValue: PropTypes.any,
  helperText: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
    PropTypes.func,
  ]),
  optional: PropTypes.bool,
  conditionalBy: PropTypes.func,
  props: PropTypes.object,
  validation: PropTypes.object,
  onSearch: PropTypes.func,
  startIcon: PropTypes.node,
  readOnly: PropTypes.bool,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  allowAdd: PropTypes.bool,
  name: PropTypes.string,
  getValue: PropTypes.func,
  isSearching: PropTypes.bool,
  accept: PropTypes.string,
};

const commonProps = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  element: PropTypes.any,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  showActions: PropTypes.bool,
  fieldSet: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.shape(formField), PropTypes.bool]),
  ).isRequired,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  buttonsProps: PropTypes.shape({
    submit: PropTypes.shape({
      label: PropTypes.string,
      color: PropTypes.string,
      variant: PropTypes.string,
      icon: PropTypes.any,
    }),
    cancel: PropTypes.shape({
      label: PropTypes.string,
      color: PropTypes.string,
      variant: PropTypes.string,
      icon: PropTypes.any,
    }),
    delete: PropTypes.shape({
      label: PropTypes.string,
      color: PropTypes.string,
      variant: PropTypes.string,
      icon: PropTypes.any,
    }),
  }),
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

FormBuilder.propTypes = { ...commonProps };
RenderForm.propTypes = { ...commonProps };

const defaultProps = {
  showActions: true,
  buttonsProps: {
    submit: {
      label: "Guardar",
      color: "success",
      variant: "outlined",
      icon: <SaveRounded />,
    },
    cancel: {
      label: "Cancelar",
      color: "secondary",
      variant: "outlined",
      icon: <CancelRounded />,
    },
    delete: {
      label: "Eliminar",
      color: "error",
      variant: "outlined",
      icon: <DeleteRounded />,
    },
  },
};

FormBuilder.defaultProps = { ...defaultProps, sx: {} };
RenderForm.defaultProps = { ...defaultProps };

export default FormBuilder;
export { RenderFieldInput };
